import type { WhenSelection } from "@ui/components/SearchBar";
import type { TemperatureBand } from "@ui/utils/temperature-bands";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRange, useRefinementList } from "react-instantsearch";
import { useSearchResults } from "@/hooks/use-search-results";
import { DestinationsFilters } from "./components/DestinationsSearch/components/DestinationsFilters";
import { DestinationsFiltersFooter } from "./components/DestinationsSearch/components/DestinationsFiltersFooter";
import { DESTINATION_FACETS } from "./facets";
import {
  BUDGET_MAX_CENTS,
  BUDGET_MIN_CENTS,
  type KoppenMainClass,
  type LanguageCode,
  type UtcOffset,
} from "./filter-values";
import { useBudgetHistogram } from "./use-budget-histogram";
import { whenToMonths } from "./when-to-months";

const EMPTY_WHEN: WhenSelection = { start: null, end: null };

interface UseDestinationFiltersOptions {
  /** Seeds from URL params — a shared link, back/forward, or the mobile search
   *  overlay hand-off. Read once in the state initializers (pages remount on
   *  navigation). */
  initialWhen?: WhenSelection;
  initialBands?: TemperatureBand[];
  initialBudget?: [number, number] | null;
  initialKoppen?: KoppenMainClass[];
  initialLanguages?: LanguageCode[];
  initialOffsets?: UtcOffset[];
}

export function useDestinationFilters(initial: UseDestinationFiltersOptions = {}) {
  const [whenSelection, setWhenSelection] = useState<WhenSelection>(initial.initialWhen ?? EMPTY_WHEN);
  const [selectedBands, setSelectedBands] = useState<TemperatureBand[]>(initial.initialBands ?? []);
  const [budget, setBudget] = useState<[number, number] | null>(initial.initialBudget ?? null);
  const [selectedKoppen, setSelectedKoppen] = useState<KoppenMainClass[]>(initial.initialKoppen ?? []);
  const [selectedLanguages, setSelectedLanguages] = useState<LanguageCode[]>(initial.initialLanguages ?? []);
  const [selectedOffsets, setSelectedOffsets] = useState<UtcOffset[]>(initial.initialOffsets ?? []);

  // Read once per mount rather than per render: the ranking must not reorder
  // itself mid-session, and a browse that spans midnight on the last of the
  // month keeps the list it started with until the next navigation.
  const [defaultMonth] = useState(() => new Date().getMonth() + 1);

  // The travel window as month numbers (always non-empty), falling back to the
  // month we are in — browsing without dates asks where to go NOW.
  const effectiveMonths = useMemo(() => whenToMonths(whenSelection, defaultMonth), [whenSelection, defaultMonth]);
  // The band facet needs ONE concrete month because the fields are per-month,
  // and it is the window's first — which is the current month when no dates
  // are named, so the facet and the ranking always read the same season.
  const bandFacetMonth = effectiveMonths[0];

  // The ranking anchors affordability on a ceiling, not a window: only a
  // lowered max is a statement about affordability, and a max at the bound
  // means "anchor on the corpus median" exactly like no budget at all.
  const budgetCeiling = budget && budget[1] < BUDGET_MAX_CENTS ? budget[1] : null;

  const clearFilters = useCallback(() => {
    setSelectedBands([]);
    setSelectedKoppen([]);
    setSelectedLanguages([]);
    setSelectedOffsets([]);
    setBudget(null);
  }, []);

  return {
    bandFacetMonth,
    budget,
    budgetCeiling,
    clearFilters,
    effectiveMonths,
    selectedBands,
    selectedKoppen,
    selectedLanguages,
    selectedOffsets,
    setBudget,
    setSelectedBands,
    setSelectedKoppen,
    setSelectedLanguages,
    setSelectedOffsets,
    setWhenSelection,
    whenSelection,
  };
}

export type DestinationFilters = ReturnType<typeof useDestinationFilters>;

export function DestinationsFiltersBody({
  budget,
  selectedBands,
  selectedKoppen,
  selectedLanguages,
  selectedOffsets,
  setBudget,
  setSelectedBands,
  setSelectedKoppen,
  setSelectedLanguages,
  setSelectedOffsets,
}: DestinationFilters) {
  const { distribution } = useBudgetHistogram();

  return (
    <DestinationsFilters
      budgetDistribution={distribution}
      budgetValue={budget}
      koppenValue={selectedKoppen}
      languageValue={selectedLanguages}
      onBudgetChange={setBudget}
      onKoppenChange={setSelectedKoppen}
      onLanguageChange={setSelectedLanguages}
      onTemperatureChange={setSelectedBands}
      onTimezoneChange={setSelectedOffsets}
      temperatureValue={selectedBands}
      timezoneValue={selectedOffsets}
    />
  );
}

/** The modal footer: Clear all + a "Show N destinations" button that dismisses.
 *  Clear all resets the page-local filter state — the refinement bridges then
 *  withdraw their refinements themselves; `useClearRefinements` would fight
 *  them (state would instantly re-refine what it cleared). */
export function DestinationsFiltersFooterAdapter({
  clearFilters,
  onApply,
}: {
  clearFilters: () => void;
  onApply: () => void;
}) {
  const { totalCount } = useSearchResults({ sessionKey: "destinations" });

  return <DestinationsFiltersFooter onApply={onApply} onClearAll={clearFilters} resultCount={totalCount} />;
}

// Renderless side-effects: syncs filter state into Instantsearch refinements.
// Mounted at page level so refinements stay applied whether or not the filters
// modal is open.
export function DestinationsFiltersRefinements({
  bandFacetMonth,
  budget,
  selectedBands,
  selectedKoppen,
  selectedLanguages,
  selectedOffsets,
}: DestinationFilters) {
  return (
    <>
      <BudgetRangeRefinement budget={budget} />
      {selectedBands.length > 0 ? (
        <Refinement
          attribute={DESTINATION_FACETS.tempBand(bandFacetMonth)}
          key={bandFacetMonth}
          values={selectedBands}
        />
      ) : null}
      {selectedKoppen.length > 0 ? <Refinement attribute={DESTINATION_FACETS.koppen} values={selectedKoppen} /> : null}
      {selectedOffsets.length > 0 ? (
        <Refinement attribute={DESTINATION_FACETS.utcOffset} values={selectedOffsets} />
      ) : null}
      {selectedLanguages.length > 0 ? (
        <Refinement attribute={DESTINATION_FACETS.languages} values={selectedLanguages} />
      ) : null}
    </>
  );
}

// Bridges the local budget tuple into a numeric range refinement on
// `cost_total_cents`. useRange drops a bound that equals the range edge, so the
// full range emits no filter at all — destinations without a known cost only
// drop out once a real ceiling or floor is set. The ceiling additionally
// reaches the ranking through the scoring payload in `useDestinationsSearch`.
function BudgetRangeRefinement({ budget }: { budget: [number, number] | null }) {
  const { start, refine } = useRange({
    attribute: "cost_total_cents",
    min: BUDGET_MIN_CENTS,
    max: BUDGET_MAX_CENTS,
  });

  useEffect(() => {
    const desired = budget ?? [BUDGET_MIN_CENTS, BUDGET_MAX_CENTS];
    // `start` echoes the applied refinement, with unset bounds as ±Infinity.
    const current = [
      typeof start[0] === "number" && Number.isFinite(start[0]) ? start[0] : BUDGET_MIN_CENTS,
      typeof start[1] === "number" && Number.isFinite(start[1]) ? start[1] : BUDGET_MAX_CENTS,
    ];
    // Compare before refining: an unconditional refine of the value already
    // applied would still dirty the search state and loop through this effect.
    if (current[0] !== desired[0] || current[1] !== desired[1]) {
      refine([desired[0], desired[1]]);
    }
  }, [budget, start, refine]);

  return null;
}

// useRefinementList's items don't always reflect facet values absent from the
// current aggregation, so we track what we've already refined locally to avoid
// double-toggling (which would cancel the refinement and create an infinite loop).
function Refinement<T extends string | number>({ attribute, values }: { attribute: string; values: readonly T[] }) {
  const { refine } = useRefinementList({ attribute, operator: "or" });
  const refinedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const desired = new Set(values.map(String));
    const current = refinedRef.current;

    for (const value of desired) {
      if (!current.has(value)) {
        refine(value);
        current.add(value);
      }
    }

    for (const value of current) {
      if (!desired.has(value)) {
        refine(value);
        current.delete(value);
      }
    }
  }, [values, refine]);

  return null;
}
